import React, { useEffect, useState } from "react";
import { C, font } from "../lib/styles.js";
import { bridge } from "../lib/bridgeCallables.js";

/**
 * The Home screen's "Needs you" list (SafeShare plan 03, section 0's rule;
 * first built in slice 2.2): every open attention row, whatever slice wrote
 * it, in one place, so nothing left for a person depends on remembering to
 * open a tab. Admins only (the rows come from the admin-only listAttention
 * callable; anyone else sees nothing). Each row opens the app it belongs to.
 */

export const REASON_LABELS = {
  postshot_signed_out: "Postshot signed out",
  postshot_missing: "Postshot not installed",
  postshot_sign_in_unknown: "Postshot sign-in unknown",
  disk_almost_full: "Disk almost full",
  startup_not_set: "Set up startup",
  bridge_out_of_date: "Bridge out of date",
  confirm_root: "Confirm this root",
  root_facts_changed: "Root facts changed",
  // Slice 5.2 (Traffic Control → Transfers)
  order_expired: "Order not taken",
  order_lost: "Order lost three times",
  order_failed: "Order failed",
  stray_copy: "Stray copy reported",
  stray_folder: "Unknown job folder",
  copy_suspect: "Copy failed its check",
};

/** A row's title: its reason's label (order_failed_<reason> reads "Order failed: <reason>"). */
export const reasonLabel = (code) => {
  if (REASON_LABELS[code]) return REASON_LABELS[code];
  const m = /^order_failed_([a-z_]+)$/.exec(String(code || ""));
  return m ? `Order failed: ${m[1].replace(/_/g, " ")}` : code;
};

/** The app a row's link opens ("traffic/Machines#uid" → "traffic"); null when unknown. */
export const linkApp = (link) => {
  const m = /^([a-z][a-z0-9-]*)(?:\/|$)/.exec(String(link || ""));
  return m ? m[1] : null;
};

export function NeedsYouList({ rows, onOpenApp }) {
  if (!rows.length) return null;
  return (
    <section style={S.box} aria-labelledby="home-needs-you">
      <div style={S.head}>
        <span id="home-needs-you" style={S.title}>Needs you</span>
        <span style={S.count}>{rows.length}</span>
      </div>
      {rows.slice(0, 8).map((r) => {
        const app = linkApp(r.link);
        return (
          <div key={r.id} style={S.item}>
            <span style={S.dot} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 500 }}>{reasonLabel(r.reasonCode)}{r.machineLabel ? ` · ${r.machineLabel}` : ""}</div>
              <div style={S.detail}>{r.detail}</div>
            </div>
            {app && onOpenApp && <button type="button" style={S.link} onClick={() => onOpenApp(app)}>Review →</button>}
          </div>
        );
      })}
      {rows.length > 8 && <div style={S.detail}>and {rows.length - 8} more in Traffic Control → Machines</div>}
    </section>
  );
}

export default function NeedsYou({ user, onOpenApp }) {
  const [rows, setRows] = useState([]);
  // As App.jsx decides who is an admin; the callable checks the role claim itself.
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  useEffect(() => {
    if (!isAdmin) return undefined;
    let live = true;
    const load = () => bridge.listAttention().then((r) => { if (live) setRows(Array.isArray(r?.rows) ? r.rows : []); }).catch(() => {});
    load();
    const t = setInterval(load, 120_000);
    return () => { live = false; clearInterval(t); };
  }, [isAdmin]);
  if (!isAdmin) return null;
  return <NeedsYouList rows={rows} onOpenApp={onOpenApp} />;
}

const S = {
  box: { border: "1px solid #E8D8AF", background: "#FFFCF4", borderRadius: 8, padding: "12px 16px", margin: "0 auto 22px", maxWidth: 720, width: "100%", boxSizing: "border-box", fontFamily: font, color: C.text, fontSize: 13 },
  head: { display: "flex", alignItems: "center", gap: 8, marginBottom: 6 },
  title: { fontSize: 15, fontWeight: 500 },
  count: { fontSize: 10.5, fontWeight: 500, padding: "2px 8px", borderRadius: 10, background: C.surfaceAlt },
  item: { display: "flex", gap: 9, alignItems: "baseline", padding: "8px 0", borderTop: "1px solid #EEE4CF" },
  dot: { flex: "none", width: 7, height: 7, borderRadius: "50%", background: C.amber, display: "inline-block" },
  detail: { fontSize: 11.5, color: C.muted, fontWeight: 300 },
  link: { marginLeft: "auto", flex: "none", fontSize: 11.5, background: "none", border: 0, textDecoration: "underline", cursor: "pointer", fontFamily: font, color: C.text },
};
